import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useUser } from "@/lib/user";
import { api } from "@/lib/api";
import { questById, scoreLabel, QUEST_META } from "@/lib/quests";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";

const DIMENSIONS = [
  { key: "clarity", label: "Clarity" },
  { key: "psfit", label: "PS-Fit" },
  { key: "empathy", label: "Empathy" },
  { key: "growth", label: "Growth" },
  { key: "execution", label: "Execution" },
];

export default function ScoreCardPage({ params }) {
  const questId = Number(params.questId);
  const quest = questById(questId);
  const [, navigate] = useLocation();
  const { user } = useUser();
  const [progress, setProgress] = useState(null);
  const [nextProgress, setNextProgress] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    if (!quest) {
      navigate("/dashboard");
      return;
    }
    (async () => {
      try {
        const p = await api.getProgressOne(user.id, questId);
        setProgress(p);
        if (questId + 1 <= 12) {
          try {
            const np = await api.getProgressOne(user.id, questId + 1);
            setNextProgress(np);
          } catch (_) {}
        }
      } catch (e) {
        setError("Could not load score.");
      }
    })();
  }, [user, quest, questId, navigate]);

  if (!user || !quest) return null;

  const sb = progress?.score_breakdown;
  const score = progress?.score;
  const isCompleted = progress?.status === "completed" && sb;
  const label = scoreLabel(score);
  const radarData = sb
    ? DIMENSIONS.map((d) => ({ dimension: d.label, value: sb[d.key] ?? 0, full: 100 }))
    : [];

  const nextMeta = QUEST_META.find((q) => q.id === questId + 1);
  const canStartNext =
    nextMeta && nextProgress && nextProgress.status !== "locked" && nextProgress.status !== "completed";

  return (
    <main data-testid="scorecard-page" className="min-h-screen px-6 sm:px-10 lg:px-20 py-12">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate("/dashboard")}
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300">
          {quest.part} · Quest {String(questId).padStart(2, "0")}
        </p>
        <h1 data-testid="scorecard-quest-title" className="mt-2 text-3xl md:text-4xl font-semibold text-zinc-100 tracking-tight">
          {quest.title}
        </h1>

        {error && (
          <p className="mt-6 text-sm text-red-400" data-testid="scorecard-error">{error}</p>
        )}

        {!isCompleted && !error && (
          <div
            data-testid="scorecard-empty-state"
            className="mt-14 rounded-2xl border border-zinc-800 bg-zinc-950/40 p-12 text-center max-w-xl mx-auto"
          >
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-amber-500/30 bg-amber-500/[0.06] text-amber-300">
              <ArrowLeft className="h-5 w-5 rotate-180" />
            </div>
            <p className="text-zinc-200 font-medium">You haven't completed this quest yet.</p>
            <p className="mt-2 text-sm text-zinc-500">
              Finish the conversation to unlock your founder score for {quest.title}.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {progress?.status !== "locked" && (
                <Button
                  data-testid="scorecard-start-btn"
                  onClick={() => navigate(`/quest/${questId}`)}
                  className="h-10 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
                >
                  {progress?.status === "in_progress" ? "Continue quest" : "Start quest"}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              <Button
                data-testid="scorecard-empty-back"
                onClick={() => navigate("/dashboard")}
                variant="outline"
                className="h-10 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 gap-2"
              >
                Back to dashboard
              </Button>
            </div>
          </div>
        )}

        {isCompleted && (
          <div className="mt-10 grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
            {/* Score number + label */}
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-2">
                founder score
              </p>
              <div className="font-mono text-7xl text-zinc-50 leading-none" data-testid="scorecard-score">
                {Math.round(score)}
              </div>
              {label && (
                <div className={`mt-3 text-xl font-semibold ${label.color}`} data-testid="scorecard-label">
                  {label.label}
                </div>
              )}

              {/* Progress bars per dimension */}
              <div className="mt-10 space-y-4" data-testid="dimension-bars">
                {DIMENSIONS.map((d) => {
                  const v = sb[d.key] ?? 0;
                  return (
                    <div key={d.key} data-testid={`dim-${d.key}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm text-zinc-300">{d.label}</span>
                        <span className="font-mono text-xs text-amber-300">{v}/100</span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-zinc-900 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-700"
                          style={{ width: `${v}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Radar chart */}
            <div className="rounded-2xl border border-amber-500/15 bg-zinc-950/40 p-6" data-testid="radar-chart">
              <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-amber-300 mb-3">
                five dimensions
              </p>
              <div className="h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="75%">
                    <PolarGrid stroke="rgba(245,158,11,0.15)" />
                    <PolarAngleAxis
                      dataKey="dimension"
                      tick={{ fill: "#a1a1aa", fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fill: "#52525b", fontSize: 10 }}
                      stroke="transparent"
                    />
                    <Radar
                      name="score"
                      dataKey="value"
                      stroke="#f59e0b"
                      fill="#f59e0b"
                      fillOpacity={0.35}
                      animationDuration={800}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {isCompleted && (
          <div className="mt-12 flex flex-wrap gap-3">
            <Button
              onClick={() => navigate("/dashboard")}
              data-testid="scorecard-dashboard-btn"
              variant="outline"
              className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 gap-2 h-11"
            >
              Back to dashboard
            </Button>
            {canStartNext && (
              <Button
                onClick={() => navigate(`/quest/${questId + 1}`)}
                data-testid="scorecard-next-btn"
                className="h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
              >
                Start next quest <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
